/**
 * Gating for the public quote's deposit payment CTA.
 *
 * Two orders exist:
 *
 *  - Approve and pay (the customer's own page, public or portal): the customer
 *    approves (signs) first, whether or not the deposit is marked "Required to
 *    approve", and once the effective status is APPROVED (and a deposit is
 *    still due) the Pay-Deposit options open. A required deposit still holds
 *    the job back until it is paid — that is the server's rule, not this
 *    page's.
 *  - Deposit before approval (pass `depositRequired`; the staff in-person
 *    proposal checkout only): the deposit is collected BEFORE approval — the
 *    CTA shows while the quote is still actionable, and approval stays locked
 *    until the deposit is paid — held, or a bank payment the processor
 *    accepted that is still clearing.
 *
 * Extracted as a pure function so the gating is unit-testable independently of
 * the large public quote route component.
 */
export function shouldShowDepositCta(args: {
  depositAmount: number | null | undefined;
  depositCollected: boolean;
  /** The status after any in-session approve/decline action. */
  effectiveStatus: string;
  /** The estimate's "Required to approve" flag. */
  depositRequired?: boolean | null;
  /** The quote can still be acted on (actionable status, not past valid-until). */
  actionable?: boolean;
  /** A bank payment for the deposit is clearing — nothing more to pay. */
  depositClearing?: boolean;
}): boolean {
  if (!isDepositOwed(args)) return false;
  // A converted estimate's deposit is still owed: approval can turn the
  // estimate into a job before the customer pays, and the job needs it.
  if (args.effectiveStatus === 'APPROVED' || args.effectiveStatus === 'CONVERTED') return true;
  return Boolean(args.depositRequired) && Boolean(args.actionable);
}

/**
 * Whether a deposit is still owed: a positive amount, not collected, and no
 * bank payment for it clearing. On the customer's own page this is what makes
 * the primary action read "Approve and pay".
 */
export function isDepositOwed(args: {
  depositAmount: number | null | undefined;
  depositCollected: boolean;
  depositClearing?: boolean;
}): boolean {
  return (Number(args.depositAmount) || 0) > 0 && !args.depositCollected && !args.depositClearing;
}

/**
 * Whether approval is currently locked behind the deposit: the deposit is
 * required to approve and it has not been paid yet. A bank payment still
 * clearing counts as paid — the server accepts the approval.
 */
export function isApprovalBlockedByDeposit(args: {
  depositAmount: number | null | undefined;
  depositCollected: boolean;
  depositRequired?: boolean | null;
  depositClearing?: boolean;
}): boolean {
  return isDepositOwed(args) && Boolean(args.depositRequired);
}

/**
 * The surcharge preview the server computed for ONE deposit amount, re-priced
 * for the amount the customer is about to be charged.
 *
 * A pre-approval ("Required to approve") deposit is priced on the add-ons the
 * customer ticks on the page, so the base moves client-side while the preview
 * still describes the stored deposit. Card totals and the fee disclosure must
 * follow the base, or the page discloses a total the charge will not match.
 * Rounding mirrors the server's `computeSurchargeCents`: integer cents times
 * the already-capped disclosed rate, rounded to the cent.
 */
export function rescaleDepositSurcharge<
  T extends {
    enabled: boolean;
    surchargePercent: number;
    surchargeAmount: number;
    totalWithSurcharge: number;
  },
>(surcharge: T | undefined, depositAmount: number): T | undefined {
  if (!surcharge) return surcharge;
  const depositCents = Math.round((Number(depositAmount) || 0) * 100);
  const previewBaseCents = Math.round(
    (Number(surcharge.totalWithSurcharge) - Number(surcharge.surchargeAmount)) * 100,
  );
  if (depositCents <= 0 || depositCents === previewBaseCents) return surcharge;
  const surchargeCents = surcharge.enabled
    ? Math.round(depositCents * (surcharge.surchargePercent / 100))
    : 0;
  return {
    ...surcharge,
    surchargeAmount: surchargeCents / 100,
    totalWithSurcharge: (depositCents + surchargeCents) / 100,
  };
}

/**
 * The badge beside the deposit on the public estimate page. "Required" only
 * for a deposit the estimate demands before approval; an optional one read
 * "Required" too, which told customers they had to pay what they did not.
 */
export function depositBadgeLabel(args: {
  depositCollected: boolean;
  depositClearing: boolean;
  /** The rail of the clearing payment: a bank one reads "Pending ACH". */
  depositClearingRail?: string | null;
  depositRequired?: boolean | null;
}): 'Paid' | 'Pending ACH' | 'Pending' | 'Required' | 'Due' {
  if (args.depositCollected) return 'Paid';
  if (args.depositClearing) {
    return args.depositClearingRail === 'BANK' ? 'Pending ACH' : 'Pending';
  }
  return args.depositRequired ? 'Required' : 'Due';
}
